import { useMemo, useRef } from "react";
import useSvgTextWidth from "hooks/useSvgTextWidth";
import type { Margin, ScaleLinear, ScaleTime } from "types/charts";
import useAreaChart from "../AreaChartProvider/useAreaChartStore/useAreaChartStore";
import useYAxisActions from "../YAxisProvider/useYAxisActions";
import useXAxisActions from "../XAxisProvider/useXAxisActions";

export interface RenderLabelProps {
  yScale: ScaleLinear;
  xScale: ScaleTime;
  margin: Margin;
  width: number;
  height: number;
}

export interface AnnotationPropsRenderLabel {
  renderLabel?: (props: RenderLabelProps) => React.ReactNode;
  slotProps?: never;
  yAxisId?: never;
  yValue?: never;
  xValue?: never;
  children?: never;
}

export interface AnnotationSlotProps {
  rect?: React.SVGProps<SVGRectElement>;
  text?: React.SVGProps<SVGTextElement>;
}

export interface AnnotationPropsRect {
  slotProps?: AnnotationSlotProps;
  yAxisId?: string;
  yValue: number;
  xValue: Date | number;
  children?: React.ReactNode;
  renderLabel?: never;
}

export type AnnotationProps = AnnotationPropsRect | AnnotationPropsRenderLabel;

const MIN_WIDTH = 24;
const PADDING_X = 6;

function Annotation(props: AnnotationProps) {
  const { xValue, yValue, yAxisId, slotProps } = props;
  const { margin, width: chartWidth, height } = useAreaChart();
  const { getYScale } = useYAxisActions();
  const { getXScale } = useXAxisActions();

  const textRef = useRef<SVGTextElement>(null);
  const textWidth = useSvgTextWidth(textRef, props.children);
  const width = Math.max(MIN_WIDTH, textWidth + PADDING_X * 2);

  const yScale = getYScale(yAxisId);
  const xScale = getXScale();

  // Sits left of the point, flipping right when that would overflow the chart start, then clamped to the chart end.
  const rectX = useMemo(() => {
    if (xValue === undefined) return 0;
    const pointX = xScale(xValue);
    const preferredX = pointX - width >= 0 ? pointX - width : pointX;
    return Math.max(0, Math.min(preferredX, chartWidth - width));
  }, [chartWidth, width, xScale, xValue]);

  if (props.renderLabel) {
    return <>{props.renderLabel({ yScale, xScale, margin, width: chartWidth, height })}</>;
  }

  if (!yScale || !yValue) {
    return null;
  }

  return (
    <g>
      <rect x={rectX} y={yScale(yValue) - 24} rx={10} width={width} height={18} {...slotProps?.rect} />
      <text
        ref={textRef}
        x={rectX + width / 2}
        y={yScale(yValue)}
        dy={-14}
        fill="#FFF"
        fontSize={12}
        fontWeight="500"
        textAnchor="middle"
        dominantBaseline="middle"
        alignmentBaseline="middle"
        {...slotProps?.text}
      >
        {props.children}
      </text>
    </g>
  );
}

export default Annotation;
